import { Backdrop, makeStyles, Modal } from '@material-ui/core'
import { TextArea } from '@progress/kendo-react-inputs'
import {
    SetStateAction,
    useEffect,
    // useRef,
    useState,
} from 'react'
import { useAppSelector } from '../../app/hooks'
import { requestQuoteChanges } from '../../features/order/orderSlice'
import styles from './RequestQuoteChangesModal.module.scss'
import { BUTTON_TYPES, AWCButton } from '../../GlobalComponents/Inputs/Inputs'
import { useDispatch } from 'react-redux'
import { DatePicker } from '../../GlobalComponents/DateInput/DateInput'

import { getCurrentUser } from '../../features/meta/metaSlice'

import { fqAlert } from '../../GlobalComponents/NotificationManager/NotificationManager'
import { getAPIURL } from '../../GlobalComponents/URLs'

const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: 0,
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
}))

interface RequestQuoteChangesModalProps {
    close(): void
}

export const RequestQuoteChangesModal = (props: RequestQuoteChangesModalProps) => {
    const classes = useStyles()
    const { close } = props
    let order = useAppSelector((state) => state.order.orderHeader[0])!
    const _attachments = useAppSelector((state) => state.order.orderFilenames)
    const attachments = _attachments.filter((row) => !row.Internal)
    const [files, setFiles] = useState(attachments)
    const dispatch = useDispatch()

    const [comments, setComments] = useState('')

    let earliestNeedDate: SetStateAction<string | null | undefined> = null
    let needDates = order.OrderDetailView.filter(
        (d) => d.OriginalOnDockDate !== null && typeof d.OriginalOnDockDate !== 'undefined'
    )
    if (needDates.length > 0)
        earliestNeedDate = needDates.reduce((a, b) => {
            return new Date(a.OriginalOnDockDate ?? '') < new Date(b.OriginalOnDockDate ?? '')
                ? a
                : b
        }).OriginalOnDockDate

    //Math.min.apply(null, needDates)
    const [needDate, setNeedDate] = useState(earliestNeedDate)
    const [needDateUpdated, setNeedDateUpdated] = useState(false)
    const [quoteExpirationDate, setQuoteExpirationDate] = useState(order.QuoteExpirationDate)
    const [quoteExpirationDateUpdated, setQuoteExpirationDateUpdated] = useState(false)

    const title = 'Request Changes'
    const [loading, setLoading] = useState(true)

    const dataSheetParts = useAppSelector((state) => {
        let toInclude = state.order.orderHeader[0].OrderDetailView.filter(
            (row) => row.includeDataSheets
        )
        let processed = toInclude.map((row) => ({
            partKey: row.PartKey!,
            partNumber: row.PartNumber!,
        }))
        let alreadyIn: Record<number, boolean> = {}
        let clean = processed.filter((row) => row.partKey && row.partNumber)
        let final: typeof clean = []
        clean.forEach((row) => {
            if (alreadyIn[row.partKey]) {
                return
            }
            alreadyIn[row.partKey] = true
            final.push(row)
        })
        return final
    })

    const [dataSheets, setDataSheets] = useState<any[]>([])
    // TODO this is done on line item change now for graying out input -- can just get it from there
    useEffect(() => {
        const getDataSheets = async () => {
            if (dataSheetParts.length === 0) {
                return
            }
            let dataSheets = await Promise.all(
                dataSheetParts.map(async (info) => {
                    let url = getAPIURL() + 'inventory/parts/datasheets/' + info.partKey
                    let req = await fetch(url, { credentials: 'include' })
                    let json = await req.json()
                    return { DataSheets: json, name: `${info.partNumber}` }
                })
            )
            let currentUser = await getCurrentUser()
            let company = currentUser.CompanyCode
            if (company !== 'A' && company !== 'C') {
                fqAlert({
                    type: 'error',
                    title: 'Invalid Company',
                    message: `Can't get data sheets for company code ${company}`,
                })
                return
            }
            let baseUrl =
                currentUser.CompanyCode === 'C'
                    ? 'https://www.a3im.com'
                    : 'https://www.awc-inc.com/'
            let flattenedDataSheets: Array<{ url: string; name: string }> = []
            dataSheets.forEach((row) => {
                if (row.DataSheets.length === 0) {
                    return
                }
                if (row.DataSheets.length > 1) {
                    row.DataSheets.forEach((sheet: string, i: number) => {
                        flattenedDataSheets.push({
                            url: baseUrl + sheet,
                            name: row.name + ` - ${i + 1}`,
                        })
                    })
                } else {
                    flattenedDataSheets.push({
                        url: baseUrl + row.DataSheets[0],
                        name: row.name,
                    })
                }
            })
            setDataSheets(flattenedDataSheets)
        }
        getDataSheets()
    }, [])
    //probably should just return the new state from the saveOrder dispatch and initialize in one use effect
    useEffect(() => {
        ;(async function () {
            setLoading(false)
        })()
    }, [])
    useEffect(() => {
        if (!loading) {
            ;(async function () {
                setFiles(attachments)
            })()
        }
    }, [loading])
    return (
        <Modal
            open={true}
            className={classes.modal}
            // reason will be populated if they clicked off the modal to close
            onClose={(e: any, reason: string) => !reason && close()}
            BackdropComponent={Backdrop}
            // kendo uses 99 for dropdowns
            style={{ zIndex: 88 }}
            // !!! kendo dropdowns will instantly close if this isn't disabled. The drop down renders outside the modal and takes focus out, but the focus trap will take it back
            disableEnforceFocus
        >
            <div className={styles.modalContainer}>
                {loading && (
                    <div className={styles.requestQuoteChangesModal} style={{ minWidth: 800 }}>
                        {' '}
                        <div className={'centered'} style={{ height: '100%' }}>
                            <div className={'k-i-loading'} style={{ fontSize: 200 }} />
                        </div>
                    </div>
                )}
                {!loading && (
                    <div
                        className={styles.requestQuoteChangesModal}
                        style={{ display: 'flex', flexDirection: 'column' }}
                    >
                        {/* Line Item Notes Header */}
                        <div className={styles.requestQuoteChangesModalHeader}>
                            <div className={styles.requestQuoteChangesModalTitle}>{title}</div>
                            <div onClick={close} className={'a-i-close'} />
                        </div>
                        <div className={'a-divider'} style={{ marginBottom: 15 }} />
                        <div style={{ display: 'flex', padding: '1rem' }}>
                            <div>
                                <div className={styles.dateChanges}>
                                    <div
                                        className={'align-right'}
                                        style={{
                                            alignSelf: 'start',
                                            marginRight: 10,
                                            fontWeight: 500,
                                        }}
                                    >
                                        Reference:
                                    </div>
                                    <div style={{ alignSelf: 'start' }}>
                                        {order.RFQNumber === ''
                                            ? order.OrderNumber
                                            : order.RFQNumber}
                                    </div>
                                    <div
                                        className={'align-right'}
                                        style={{ marginRight: 10, fontWeight: 500 }}
                                    >
                                        Need Date:
                                    </div>
                                    <div>
                                        <DatePicker
                                            minDate={new Date()}
                                            value={needDate}
                                            onChange={(newDate: any) => {
                                                setNeedDate(newDate)
                                                setNeedDateUpdated(true)
                                            }}
                                            onValidityChange={(isValid) => {
                                                if (!isValid) {
                                                    setNeedDate(undefined)
                                                    setNeedDateUpdated(false)
                                                }
                                            }}
                                            style={{
                                                backgroundColor: 'initial',
                                                fontSize: '14px',
                                            }}
                                        />
                                    </div>
                                    <div
                                        className={'align-right'}
                                        style={{ marginRight: 10, fontWeight: 500 }}
                                    >
                                        Quote Expiration:
                                    </div>
                                    <div>
                                        <DatePicker
                                            minDate={new Date()}
                                            onChange={(newDate: any) => {
                                                setQuoteExpirationDate(newDate)
                                                setQuoteExpirationDateUpdated(true)
                                            }}
                                            onValidityChange={(isValid) => {
                                                if (!isValid) {
                                                    setQuoteExpirationDate(undefined)
                                                    setQuoteExpirationDateUpdated(false)
                                                }
                                            }}
                                            value={quoteExpirationDate}
                                            style={{
                                                backgroundColor: 'initial',
                                                fontSize: '14px',
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div style={{ marginBottom: 5, fontWeight: 500 }}>Comments:</div>
                                <div>
                                    <TextArea
                                        rows={12}
                                        style={{
                                            width: 325,
                                            height: 100,
                                            borderWidth: 1,
                                            borderColor: 'lightgray',
                                            backgroundColor: 'white',
                                        }}
                                        onChange={(event: any) => {
                                            setComments(event.value)
                                        }}
                                        value={comments}
                                    />
                                </div>
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto auto',
                                columnGap: '10px',
                                marginTop: 10,
                                justifyContent: 'end',
                                padding: '0 2rem',
                            }}
                        >
                            <div
                                className={'a-cancel-button'}
                                style={{
                                    width: 150,
                                }}
                                onClick={close}
                                data-testid="lineItemNotesModalCancelButton"
                            >
                                Cancel
                            </div>
                            <AWCButton
                                type={BUTTON_TYPES.PRIMARY}
                                onClick={async () => {
                                    if (
                                        !needDateUpdated &&
                                        !quoteExpirationDateUpdated &&
                                        comments === ''
                                    ) {
                                        fqAlert({
                                            type: 'error',
                                            title: 'Please update at least one field',
                                        })
                                        return
                                    }

                                    dispatch(
                                        requestQuoteChanges({
                                            needDate: needDateUpdated ? needDate : null,
                                            quoteExpirationDate: quoteExpirationDateUpdated
                                                ? quoteExpirationDate
                                                : null,
                                            comments: comments,
                                        })
                                    )
                                    close()
                                }}
                                style={{
                                    width: 150,
                                }}
                                data-testid="lineItemNotesModalSubmitButton"
                            >
                                Submit Request
                            </AWCButton>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    )
}
